/**
 * permessiConsoleRegole — le regole pure della pagina dei Permessi della console (`PermessiPage`): l'ordine in cui si
 * mostrano gli albi dei comuni (prima chi ha un problema) e da quando conta «questo mese». Senza database, per le prove.
 */
import type { FonteAlbo } from '@shared/permessi/albi';
import { CAPABILITY_PERMESSI } from '@shared/permessi/api';
import { categorieDeiProdotti, type IdCategoriaLavoro } from '@shared/permessi/categorieLavoro';
import { aziendaHaCapability, type TenantCapabilitySnapshot } from '@shared/capabilities/capabilityService';
import type { StatoLettore } from '@shared/gare/tipi';

/**
 * Un comune con la salute del suo albo (il lavoro `permessi_albo_<cf>` in `gare_connettori`) e della lettura del suo
 * archivio (`permessi_storico_<cf>`, PERMESSI-EDILIZI-MODEL §19.2): un archivio fermo l'orologio lo salta in silenzio.
 */
export interface AlboDelComune {
  fonte: FonteAlbo;
  lettore: StatoLettore | null;
  archivio: StatoLettore | null;
}

/** Come va la lettura dell'archivio di un comune: gli anni già letti (dal più recente) e la salute dell'ultima lettura. */
export interface ArchivioDelComune {
  anni: string[];
  stato: 'funziona' | 'a_singhiozzo' | 'fermo';
  /** L'ultima lettura si è fermata al tetto delle pagine (`-troncato` nella versione): l'anno non è letto tutto. */
  in_parte: boolean;
}

/** Null se l'archivio di quel comune non si legge (fornitore senza archivio) e non è mai stato letto. */
export function archivioDelComune(a: AlboDelComune): ArchivioDelComune | null {
  const anni = Object.keys(a.fonte.storico_letto || {}).sort((x, y) => y.localeCompare(x));
  if (!a.archivio && !anni.length) return null;
  const salute = a.archivio ? a.archivio.salute : 'buona';
  return {
    anni,
    stato: salute === 'ferma' ? 'fermo' : salute === 'degradata' ? 'a_singhiozzo' : 'funziona',
    in_parte: !!a.archivio && /-troncato$/.test(a.archivio.versione || ''),
  };
}

/** Il primo giorno del mese di adesso, come istante ISO (le date dei documenti sono ISO). */
export function inizioDelMese(adesso: Date): string {
  return new Date(Date.UTC(adesso.getUTCFullYear(), adesso.getUTCMonth(), 1)).toISOString();
}

/**
 * Prima gli albi che hanno un problema: fonte non pronta, mai letto, salute ferma, poi degradata, poi l'archivio fermo;
 * a parità per comune.
 */
export function albiInOrdine(albi: readonly AlboDelComune[]): AlboDelComune[] {
  const peso = (a: AlboDelComune): number =>
    a.fonte.stato !== 'pronta' ? 0 : !a.lettore ? 1 : a.lettore.salute === 'ferma' ? 2 : a.lettore.salute === 'degradata' ? 3 : a.archivio && a.archivio.salute === 'ferma' ? 4 : 5;
  return albi.slice().sort((a, b) => peso(a) - peso(b) || a.fonte.comune.localeCompare(b.fonte.comune, 'it'));
}

/** Un'azienda col modulo dei permessi e i prodotti per cui lo usa (piano 8, `permessi_aziende`). */
export interface AziendaDelModulo {
  id: string;
  nome: string;
  /** Null = mai impostati: l'azienda vede solo le pratiche «da verificare». */
  prodotti: string[] | null;
  categorie: IdCategoriaLavoro[];
}

/**
 * Le aziende che hanno il modulo (la stessa regola delle funzioni, `aziendaHaCapability`), coi prodotti del modulo: prima quelle
 * senza prodotti, che vanno sistemate (`functions-gare/scripts/permessi/prodotti-del-modulo.cjs`), poi per nome.
 */
export function aziendeDelModulo(
  aziende: readonly { id: string; nome: string; dati: TenantCapabilitySnapshot | null }[],
  prodottiPerAzienda: Readonly<Record<string, string[]>>,
): AziendaDelModulo[] {
  return aziende
    .filter((a) => aziendaHaCapability(a.dati, CAPABILITY_PERMESSI))
    .map((a) => {
      const prodotti = prodottiPerAzienda[a.id] || null;
      return { id: a.id, nome: a.nome, prodotti, categorie: prodotti ? categorieDeiProdotti(prodotti) : [] };
    })
    .sort((x, y) => Number(!!x.prodotti) - Number(!!y.prodotti) || x.nome.localeCompare(y.nome));
}
